import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button, Divider, Flex, Text, useToast } from '@chakra-ui/core';
import { FormHandles } from '@unform/core';

import { FiCopy } from 'react-icons/fi';
import Form from '../../../../../components/Form';
import Checkbox from '../../../../../components/Form/Checkbox';
import Input from '../../../../../components/Form/Input';
import Select from '../../../../../components/Form/Select';
import { useLayout } from '../../../../../layouts/default';
import { HTTP } from '../../../../../shared/constants';
import { useAuth } from '../../../../../shared/hooks/useAuth';
import apiGateway from '../../../../../shared/services/apiGateway';

const PagarmeSettings: React.FC = () => {
    const { store } = useAuth();
    const { changeTitle } = useLayout();

    const { id } = useParams<{ id: string }>();
    const [platform, setPlatform] = useState(null);

    const formRef = useRef<FormHandles>(null);
    const toast = useToast();
    const [enableV5, setEnableV5] = useState(false);

    const handleSubmit = useCallback(
        async (data) => {
            if (enableV5 && String(data.key).startsWith('ak')) {
                toast({
                    title: 'Chave Incompatível',
                    description: 'Sua chave Pagarme é versão 4 e você deseja utilizar versão 5',
                    status: 'error',
                    duration: 4000,
                    isClosable: true
                });

                return;
            }

            const response = await apiGateway.put(`/settings/payment_platforms/${platform.id}`, {
                ...platform,
                config: {
                    ...data,
                    isV5: enableV5,
                    isSplit: false,
                    pix_enable_mercado_pago: !!data.mercado_pago_merchant
                }
            });

            if (response.status === HTTP.STATUS.SUCCESS) {
                toast({
                    title: 'Configuração Atualizada',
                    description: 'Sua configuração foi atualizada com sucesso',
                    status: 'success',
                    duration: 4000,
                    isClosable: true
                });
            }
        },

        [platform, toast, enableV5]
    );

    const getDefaultValue = useCallback(
        (item, defaultValue = '0') => {
            return platform?.config[item] || defaultValue;
        },
        [platform]
    );

    const copyUrlWebhook = useCallback(() => {
        const webhookUrl = formRef.current.getFieldValue('webhook_url');
        navigator.clipboard.writeText(webhookUrl).then(() => {
            toast({
                title: 'Webhook Copiado',
                description: '',
                status: 'success',
                duration: 4000,
                isClosable: true
            });
        });
    }, []);

    useEffect(() => {
        changeTitle('Configurarações Pagarme');

        apiGateway.get(`/settings/payment_platforms/${id}`).then((response) => {
            setPlatform(response.data);
            setEnableV5(response.data.name === 'Pagarme V5');
        });
    }, [changeTitle, id]);

    return (
        <Form ref={formRef as any} onSubmit={handleSubmit}>
            <Flex width="100%" flexDirection="column">
                <Flex width="100%" flexDirection="column">
                    <Flex width="100%" flexDirection="column" backgroundColor="white" px="24px">
                        <Flex width="100%" py="16px" justifyContent="space-between" flexWrap="wrap">
                            <Flex width="100%" justifyContent="space-between" flexWrap="wrap">
                                <Flex width="100%" justifyContent="flex-start" alignItems="center">
                                    <Text mr="16px" fontSize="24px" color="purple.500">
                                        Configurações do Pagarme
                                    </Text>
                                </Flex>

                                {platform && (
                                    <Flex width="100%" flexDirection="column">
                                        <Flex width="50%" mt="8px">
                                            <Input
                                                name="key"
                                                label="Chave Pagarme"
                                                defaultValue={getDefaultValue('key', '')}
                                                size="sm"
                                            />
                                        </Flex>

                                        <Flex width="50%" mt="16px">
                                            <Text width="100%" fontSize="20px">
                                                Configurações de Cartão de Crédito
                                            </Text>
                                        </Flex>

                                        <Flex width="100%" mt="8px">
                                            <Flex width="48%" flexDirection="column">
                                                <Text width="100%" fontSize="20px">
                                                    Parcelamento
                                                </Text>

                                                <Select
                                                    width="200px"
                                                    name="max_installments"
                                                    label="Número máximo de parcelas"
                                                    size="sm"
                                                    defaultValue={getDefaultValue(
                                                        'max_installments',
                                                        '1'
                                                    )}
                                                >
                                                    <option>1</option>
                                                    <option>2</option>
                                                    <option>3</option>
                                                    <option>4</option>
                                                    <option>5</option>
                                                    <option>6</option>
                                                    <option>7</option>
                                                    <option>8</option>
                                                    <option>9</option>
                                                    <option>10</option>
                                                    <option>11</option>
                                                    <option>12</option>
                                                </Select>

                                                <Flex width="100%" flexWrap="wrap">
                                                    <Text width="100%">Taxas de Parcelamento</Text>

                                                    <Flex width="50%" flexDirection="column">
                                                        <Flex width="160px" mt="16px">
                                                            <Input
                                                                name="installment_1"
                                                                label="Parcela 1"
                                                                textAlign="right"
                                                                mb="0px"
                                                                size="sm"
                                                                badge="porcentage"
                                                                defaultValue={getDefaultValue(
                                                                    'installment_1'
                                                                )}
                                                            />
                                                        </Flex>

                                                        <Flex width="160px" mt="8px">
                                                            <Input
                                                                name="installment_3"
                                                                label="Parcela 3"
                                                                textAlign="right"
                                                                mb="0px"
                                                                size="sm"
                                                                badge="porcentage"
                                                                defaultValue={getDefaultValue(
                                                                    'installment_3'
                                                                )}
                                                            />
                                                        </Flex>

                                                        <Flex width="160px" mt="8px">
                                                            <Input
                                                                name="installment_5"
                                                                label="Parcela 5"
                                                                textAlign="right"
                                                                mb="0px"
                                                                size="sm"
                                                                badge="porcentage"
                                                                defaultValue={getDefaultValue(
                                                                    'installment_5'
                                                                )}
                                                            />
                                                        </Flex>

                                                        <Flex width="160px" mt="8px">
                                                            <Input
                                                                name="installment_7"
                                                                label="Parcela 7"
                                                                textAlign="right"
                                                                mb="0px"
                                                                size="sm"
                                                                badge="porcentage"
                                                                defaultValue={getDefaultValue(
                                                                    'installment_7'
                                                                )}
                                                            />
                                                        </Flex>

                                                        <Flex width="160px" mt="8px">
                                                            <Input
                                                                name="installment_9"
                                                                label="Parcela 9"
                                                                textAlign="right"
                                                                mb="0px"
                                                                size="sm"
                                                                badge="porcentage"
                                                                defaultValue={getDefaultValue(
                                                                    'installment_9'
                                                                )}
                                                            />
                                                        </Flex>

                                                        <Flex width="160px" mt="8px">
                                                            <Input
                                                                name="installment_11"
                                                                label="Parcela 11"
                                                                textAlign="right"
                                                                mb="0px"
                                                                size="sm"
                                                                badge="porcentage"
                                                                defaultValue={getDefaultValue(
                                                                    'installment_11'
                                                                )}
                                                            />
                                                        </Flex>
                                                    </Flex>

                                                    <Flex width="50%" flexDirection="column">
                                                        <Flex width="160px" mt="16px">
                                                            <Input
                                                                name="installment_2"
                                                                label="Parcela 2"
                                                                textAlign="right"
                                                                mb="0px"
                                                                size="sm"
                                                                badge="porcentage"
                                                                defaultValue={getDefaultValue(
                                                                    'installment_2'
                                                                )}
                                                            />
                                                        </Flex>

                                                        <Flex width="160px" mt="8px">
                                                            <Input
                                                                name="installment_4"
                                                                label="Parcela 4"
                                                                textAlign="right"
                                                                mb="0px"
                                                                size="sm"
                                                                badge="porcentage"
                                                                defaultValue={getDefaultValue(
                                                                    'installment_4'
                                                                )}
                                                            />
                                                        </Flex>

                                                        <Flex width="160px" mt="8px">
                                                            <Input
                                                                name="installment_6"
                                                                label="Parcela 6"
                                                                textAlign="right"
                                                                mb="0px"
                                                                size="sm"
                                                                badge="porcentage"
                                                                defaultValue={getDefaultValue(
                                                                    'installment_6'
                                                                )}
                                                            />
                                                        </Flex>

                                                        <Flex width="160px" mt="8px">
                                                            <Input
                                                                name="installment_8"
                                                                label="Parcela 8"
                                                                textAlign="right"
                                                                mb="0px"
                                                                size="sm"
                                                                badge="porcentage"
                                                                defaultValue={getDefaultValue(
                                                                    'installment_8'
                                                                )}
                                                            />
                                                        </Flex>

                                                        <Flex width="160px" mt="8px">
                                                            <Input
                                                                name="installment_10"
                                                                label="Parcela 10"
                                                                textAlign="right"
                                                                mb="0px"
                                                                size="sm"
                                                                badge="porcentage"
                                                                defaultValue={getDefaultValue(
                                                                    'installment_10'
                                                                )}
                                                            />
                                                        </Flex>

                                                        <Flex width="160px" mt="8px">
                                                            <Input
                                                                name="installment_12"
                                                                label="Parcela 12"
                                                                textAlign="right"
                                                                mb="0px"
                                                                size="sm"
                                                                badge="porcentage"
                                                                defaultValue={getDefaultValue(
                                                                    'installment_12'
                                                                )}
                                                            />
                                                        </Flex>
                                                    </Flex>
                                                </Flex>
                                            </Flex>
                                            <Flex width="48%" flexDirection="column">
                                                <Flex width="100%" flexDirection="column" mb="16px">
                                                    <Text fontSize="20px">Dados Bancários</Text>
                                                    <Text fontSize="16px" fontWeight="400">
                                                        Use os mesmos dados que utilizou ao criar
                                                        sua conta na pagarme.
                                                    </Text>
                                                </Flex>

                                                <Flex width="100%" justifyContent="space-between">
                                                    <Flex width="48%" flexDirection="column">
                                                        <Input
                                                            name="bank_code"
                                                            label="Código do Banco"
                                                            size="sm"
                                                            defaultValue={getDefaultValue(
                                                                'bank_code',
                                                                ''
                                                            )}
                                                        />

                                                        <Input
                                                            name="bank_account"
                                                            label="Número da Conta"
                                                            size="sm"
                                                            defaultValue={getDefaultValue(
                                                                'bank_account',
                                                                ''
                                                            )}
                                                        />

                                                        <Input
                                                            name="bank_agency"
                                                            label="Agência"
                                                            mb="0px"
                                                            size="sm"
                                                            defaultValue={getDefaultValue(
                                                                'bank_agency',
                                                                ''
                                                            )}
                                                        />
                                                    </Flex>

                                                    <Flex width="48%" flexDirection="column">
                                                        <Select
                                                            name="bank_account_type"
                                                            label="Tipo de Conta"
                                                            size="sm"
                                                            defaultValue={getDefaultValue(
                                                                'bank_account_type',
                                                                ''
                                                            )}
                                                        >
                                                            <option>Conta Corrente</option>
                                                            <option>Conta Poupança</option>
                                                        </Select>
                                                        <Input
                                                            name="bank_account_dv"
                                                            label="Conta DV"
                                                            size="sm"
                                                            defaultValue={getDefaultValue(
                                                                'bank_account_dv',
                                                                ''
                                                            )}
                                                        />

                                                        <Input
                                                            name="bank_agency_dv"
                                                            label="Agência DV"
                                                            mb="0px"
                                                            size="sm"
                                                            defaultValue={getDefaultValue(
                                                                'bank_agency_dv',
                                                                ''
                                                            )}
                                                        />
                                                    </Flex>
                                                </Flex>

                                                <Flex
                                                    width="100%"
                                                    mt="40px"
                                                    justifyContent="space-between"
                                                    wrap="wrap"
                                                >
                                                    <Flex width="48%">
                                                        <Input
                                                            name="document"
                                                            label="CNPJ"
                                                            defaultValue={getDefaultValue(
                                                                'document',
                                                                ''
                                                            )}
                                                        />
                                                    </Flex>

                                                    <Flex width="48%">
                                                        <Input
                                                            name="legal_name"
                                                            label="Razão Social"
                                                            defaultValue={getDefaultValue(
                                                                'legal_name',
                                                                ''
                                                            )}
                                                        />
                                                    </Flex>

                                                    <Flex
                                                        width="208px"
                                                        flexDirection="column"
                                                        mb="16px"
                                                    >
                                                        <Input
                                                            name="comission"
                                                            label=""
                                                            defaultValue={getDefaultValue(
                                                                'comission',
                                                                '0'
                                                            )}
                                                            hidden
                                                            isReadOnly
                                                            size="sm"
                                                            badge="porcentage"
                                                            mb="0px"
                                                        />
                                                    </Flex>
                                                </Flex>
                                            </Flex>
                                        </Flex>
                                        <Flex
                                            width="100%"
                                            justifyContent="space-between"
                                            flexWrap="wrap"
                                        >
                                            <Flex width="100%" mt="16px" mb="8px">
                                                <Text width="100%" fontSize="20px">
                                                    Configurações PIX
                                                </Text>
                                            </Flex>

                                            <Flex width="50%" flexDirection="column">
                                                <Flex
                                                    width="100%"
                                                    alignItems="end"
                                                    style={{ gap: '8px' }}
                                                >
                                                    <Flex width="64%">
                                                        <Input
                                                            name="webhook_url"
                                                            label="URL Webhook Pix"
                                                            size="sm"
                                                            mb="0px"
                                                            isReadOnly
                                                            defaultValue={`https://sandbox.env.eflorista.com.br/api/v1/pagarme/webhook/${store}`}
                                                        />
                                                    </Flex>

                                                    <Button
                                                        color="white"
                                                        size="sm"
                                                        _hover={{ backgroundColor: 'green.500' }}
                                                        _focus={{ backgroundColor: 'green.500' }}
                                                        backgroundColor="green.500"
                                                        onClick={() => copyUrlWebhook()}
                                                    >
                                                        <FiCopy size={18} />
                                                    </Button>
                                                </Flex>

                                                <Flex width="100%" flexDirection="column" mt="8px">
                                                    <Checkbox
                                                        name="is_enable_v5"
                                                        isChecked={enableV5}
                                                        onChange={() => setEnableV5(!enableV5)}
                                                        value="0"
                                                        mr="16px"
                                                    >
                                                        <Text fontSize="14px">
                                                            Utilizar Pagarme Versão 5
                                                        </Text>
                                                    </Checkbox>
                                                </Flex>

                                                <Flex
                                                    width="80%"
                                                    flexDirection="column"
                                                    mt="8px"
                                                    border="2px dashed"
                                                    backgroundColor="yellow.100"
                                                    borderColor="yellow.500"
                                                    p="8px"
                                                >
                                                    <Text>Atenção</Text>
                                                    <Text fontSize="14px" fontWeight="500">
                                                        Esteja ciente que só é possível utilizar PIX
                                                        na versão 5 do Pagarme.
                                                    </Text>
                                                </Flex>
                                            </Flex>

                                            <Flex width="50%">
                                                <Flex width="100%" flexDirection="column">
                                                    <Input
                                                        name="mercado_pago_merchant"
                                                        label="Mercado Pago Merchant KEY (PIX)"
                                                        defaultValue={getDefaultValue(
                                                            'mercado_pago_merchant',
                                                            ''
                                                        )}
                                                        size="sm"
                                                        mb="0px"
                                                    />

                                                    <Flex width="100%">
                                                        <Button
                                                            type="submit"
                                                            width="100%"
                                                            backgroundColor="purple.500"
                                                            height="48px"
                                                            mt="24px"
                                                            color="white"
                                                            _hover={{
                                                                backgroundColor: 'green.500'
                                                            }}
                                                            fontSize="24px"
                                                            fontWeight="400"
                                                            borderRadius="2px"
                                                        >
                                                            Confirmar
                                                        </Button>
                                                    </Flex>
                                                </Flex>
                                            </Flex>
                                        </Flex>
                                    </Flex>
                                )}
                            </Flex>
                            <Divider my="0px" />
                        </Flex>
                        <Divider my="0px" />
                    </Flex>
                </Flex>
            </Flex>
        </Form>
    );
};

export default PagarmeSettings;
